/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import {
  X,
  Route,
  Search,
  Filter,
  Cloud,
  Network,
  Globe,
  GitBranch,
  Cpu,
  Link2,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import type { TGWRouteTableNodeData } from './types';
import { ATTACHMENT_STYLES } from './types';

// =============================================================================
// ATTACHMENT TYPE ICON
// =============================================================================

const AttachmentTypeIcon: React.FC<{ type?: string; className?: string }> = ({ 
  type, 
  className = 'w-4 h-4' 
}) => {
  const style = type ? ATTACHMENT_STYLES[type] : null;
  const iconColor = style?.iconColor || 'text-gray-500';
  
  switch (type) {
    case 'vpc':
      return <Cloud className={`${className} ${iconColor}`} />;
    case 'direct-connect-gateway':
      return <Network className={`${className} ${iconColor}`} />;
    case 'vpn':
      return <Globe className={`${className} ${iconColor}`} />;
    case 'peering':
      return <GitBranch className={`${className} ${iconColor}`} />;
    case 'connect':
      return <Cpu className={`${className} ${iconColor}`} />;
    default:
      return <Link2 className={`${className} text-gray-500`} />;
  }
};

// =============================================================================
// ROUTE STATE BADGE
// =============================================================================

const RouteStateBadge: React.FC<{ state: string }> = ({ state }) => {
  const config: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    'active': {
      bg: 'bg-emerald-100',
      text: 'text-emerald-700',
      icon: <CheckCircle2 className="w-3 h-3" />
    },
    'blackhole': {
      bg: 'bg-red-100',
      text: 'text-red-700',
      icon: <XCircle className="w-3 h-3" />
    },
    'pending': {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      icon: <AlertCircle className="w-3 h-3" />
    },
    'deleting': {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      icon: <AlertCircle className="w-3 h-3" />
    }
  };

  const c = config[state] || config['pending'];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {state}
    </span>
  );
};

// =============================================================================
// ROUTE TYPE BADGE
// =============================================================================

const RouteTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const isStatic = type === 'static';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
      isStatic ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
    }`}>
      {type}
    </span>
  );
};

// =============================================================================
// TGW ROUTE TABLE MODAL
// =============================================================================

interface TGWRouteTableModalProps {
  data: TGWRouteTableNodeData;
  onClose: () => void;
}

export const TGWRouteTableModal: React.FC<TGWRouteTableModalProps> = ({ data, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'static' | 'propagated'>('all');
  const [filterState, setFilterState] = useState<'all' | 'active' | 'blackhole'>('all');

  // Sort and filter routes
  const filteredRoutes = useMemo(() => {
    let routes = [...data.routes];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      routes = routes.filter(r => 
        r.destination_cidr_block?.toLowerCase().includes(term) ||
        r.attachment_resource_id?.toLowerCase().includes(term) ||
        r.attachment_id?.toLowerCase().includes(term)
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      routes = routes.filter(r => r.type === filterType);
    }
    
    // Apply state filter
    if (filterState !== 'all') {
      routes = routes.filter(r => r.state === filterState);
    }
    
    // Sort by destination CIDR
    routes.sort((a, b) => {
      const aIp = a.destination_cidr_block || '';
      const bIp = b.destination_cidr_block || '';
      return aIp.localeCompare(bIp);
    });
    
    return routes;
  }, [data.routes, searchTerm, filterType, filterState]);

  // Route summary stats
  const stats = useMemo(() => ({
    total: data.routes.length,
    static: data.routes.filter(r => r.type === 'static').length,
    propagated: data.routes.filter(r => r.type === 'propagated').length,
    active: data.routes.filter(r => r.state === 'active').length,
    blackhole: data.routes.filter(r => r.state === 'blackhole').length,
  }), [data.routes]);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Route className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{data.name}</h2>
              <p className="text-xs text-gray-500 font-mono">{data.id}</p>
            </div>
            {data.isDefault && (
              <span className="px-2 py-1 text-xs bg-purple-500 text-white rounded-full font-medium">
                Default Route Table
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-4 text-sm">
          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
            {stats.total} routes
          </span>
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
            {stats.static} static
          </span>
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
            {stats.propagated} propagated
          </span>
          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full">
            {stats.active} active
          </span>
          {stats.blackhole > 0 && (
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full">
              {stats.blackhole} blackhole
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search CIDR or resource..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Types</option>
              <option value="static">Static</option>
              <option value="propagated">Propagated</option>
            </select>
          </div>
          
          {/* State Filter */}
          <select
            value={filterState}
            onChange={(e) => setFilterState(e.target.value as any)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All States</option>
            <option value="active">Active</option>
            <option value="blackhole">Blackhole</option>
          </select>
        </div>

        {/* Routes Table */}
        <div className="flex-1 overflow-auto p-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Destination</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Target</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">State</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRoutes.map((route, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-gray-900">
                    {route.destination_cidr_block || 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <AttachmentTypeIcon type={route.attachment_resource_type} />
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-gray-700 truncate">
                          {route.attachment_resource_id || route.attachment_id || 'blackhole'}
                        </div>
                        {route.attachment_resource_type && (
                          <div className="text-[10px] text-gray-500">
                            {ATTACHMENT_STYLES[route.attachment_resource_type]?.label || route.attachment_resource_type}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <RouteTypeBadge type={route.type} />
                  </td>
                  <td className="px-4 py-3">
                    <RouteStateBadge state={route.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredRoutes.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No routes match the current filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TGWRouteTableModal;

